/**
 * Section 08 — RockBand Strategy.
 *
 * R119 rewrite. RockBand now signals purely off the chart-TF candle COLOUR
 * (OverView zone) + the ALWAYS-HTF background ZONE (the mapped Higher
 * Timeframe's OverView zone). The earlier AvgCloud / Cond 1·2·3 / split
 * machinery (R114–R118) is fully retired. ISL / Target computation is
 * unchanged. Strength (High/Medium/Low) is a LABEL only.
 */
import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText } from '@mui/material';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);

const Section_strategy_rockband: GuideSection = {
  id: 'strategy-rockband',
  label: 'RockBand Strategy',
  group: 'strategies',
  icon: <GraphicEqIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>RockBand Strategy</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        A two-timeframe trend strategy. The signal looks at just two things on every closed bar: the
        <em> candle colour</em> on the chart timeframe and the <em>background colour</em> — which is
        <strong> always the mapped Higher Timeframe (HTF)</strong>. Both come from the same –100…+100
        OverView score that the on-chart OverView meter shows; the candle uses the chart-TF zone, the
        background uses the HTF zone. The HTF used for signals never changes, even if you switch the
        chart's Bkground display method in Chart Settings.
      </Typography>

      <GuideCallout kind="info" title="Evolution at a glance">
        <strong>R96 / earlier:</strong> classic Bollinger-band breakout scanner. Retired.<br />
        <strong>R97 → R113:</strong> OverView trend strategy (chart-TF score + HTF alignment).<br />
        <strong>R114 → R118:</strong> AvgCloud-based stateful entries (Cond 1·2·3) + same-direction split
        re-arms. Retired.<br />
        <strong>R119:</strong> candle colour + ALWAYS-HTF background zone. No cloud, no Cond 3,
        no splits. Each entry carries a High / Medium / Low <em>strength label</em>.<br />
        <strong>R123 (current):</strong> same candle-colour + HTF-zone logic — but the OverView score behind both
        is now built from <strong>14 toggleable constituents</strong> (see below), each of which can be switched
        on/off and weighted.
      </GuideCallout>

      <GuideCallout kind="warning" title="R123 — what the OverView score (and therefore RockBand) is made of">
        RockBand's candle colour and HTF background are just the OverView <code>−100…+100</code> score bucketed into
        zones. That score is the weighted average of up to <strong>14 constituents</strong>: EMAs, AvgCloud, CPR,
        C-POC, H-POC, RelStr, ChgN%, MACD, JHM, WR%, and (new in R123) <strong>BO_D, BO_W, Vol, AV_Band</strong>.
        Each is toggled on/off and weighted in <strong>Chart Settings → Indicators → OverView → Contributors</strong>{' '}
        (off = excluded everywhere). All constituents compute on the <em>raw</em> OHLC series (candle-type
        independent) and CPR/POC use the standard base period (≤15m → Daily, ≤4h → Weekly, &gt;4h → Monthly), so the
        chart, Scan, Backtest and Auto-Trade all use the identical score. <strong>Because of this, toggling a
        constituent, changing a weight, or changing the strong/probable thresholds shifts the live RockBand signals
        — always re-run a Backtest after such a change before relying on Auto-Trade.</strong>
      </GuideCallout>

      <H id="building-blocks">Building blocks</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Candle colour (chart-TF)</strong> — the chart timeframe's OverView zone. A candle is <em>Green</em> when score ≥ Strong (default +60, Strong Buy) and <em>Red</em> when ≤ –Strong (Strong Short). These are the only candle colours that trigger signals.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>Background zone (ALWAYS HTF)</strong> — the OverView zone of the HTF bar that <em>contains</em> the current candle's start time. The HTF is the one shown in the OverView panel (the per-user Timeframe Mapping, e.g. 90m → 1D). Five zones, in trend order: <strong>Green</strong> (Strong Buy) › <strong>Blue</strong> (Probable Buy) › <strong>Black</strong> (Neutral) › <strong>Orange</strong> (Probable Short) › <strong>Red</strong> (Strong Short).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>"Turned from" direction</strong> — for the medium / low strengths the background must be <em>trending the right way</em>: BUY needs the HTF zone to have moved <em>up</em> into its current zone (from a more-bearish zone); SHORT needs it to have moved <em>down</em>. This is judged against the previous <em>distinct</em> HTF zone.</>} />
        </ListItem>
      </List>
      <GuideCallout kind="info" title="Background is independent of the chart's Bkground setting">
        Chart Settings → Bkground has two display methods (Overview_CTF and Overview_HTF). That setting
        only changes what you <em>see</em> painted behind the candles — it never changes RockBand's signal
        computation, which always uses the HTF zone.
      </GuideCallout>

      <H id="entry-rules">Entry signals + strength</H>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Signal</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Candle</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Background (HTF zone)</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Strength</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell rowSpan={3}><strong>BUY</strong></TableCell>
            <TableCell rowSpan={3}>Green (score ≥ +Strong)</TableCell>
            <TableCell>Green (Strong Buy)</TableCell>
            <TableCell><strong>High</strong></TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Blue (Probable Buy), turned up from Black / Orange / Red</TableCell>
            <TableCell>Medium</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Black (Neutral), turned up from Orange / Red</TableCell>
            <TableCell>Low</TableCell>
          </TableRow>
          <TableRow>
            <TableCell rowSpan={3}><strong>SHORT</strong></TableCell>
            <TableCell rowSpan={3}>Red (score ≤ –Strong)</TableCell>
            <TableCell>Red (Strong Short)</TableCell>
            <TableCell><strong>High</strong></TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Orange (Probable Short), turned down from Black / Blue / Green</TableCell>
            <TableCell>Medium</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Black (Neutral), turned down from Blue / Green</TableCell>
            <TableCell>Low</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <P>
        If the candle is Green but the background is none of the above (e.g. Orange/Red, or Blue/Black
        trending the wrong way) there is <em>no BUY</em> — only a COVER if a short was open. The mirror
        holds for Red candles and SHORT.
      </P>

      <H id="exit-rules">Exit signals</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>SELL</strong> — the candle turns Red while a LONG is open and no qualifying SHORT entry fires. Closes the long to FLAT. (If a SHORT <em>does</em> qualify, the position flips long → short in one step.)</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<><strong>COVER</strong> — the candle turns Green while a SHORT is open and no qualifying BUY fires. Closes the short to FLAT. (A qualifying BUY flips short → long.)</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary={<>The Trailing-SL system (per-trade Auto-Trail widget) runs in parallel and may exit earlier on the operative TSL line.</>} />
        </ListItem>
      </List>

      <H id="signal-payload">Signal payload</H>
      <Table size="small" sx={{ my: 1 }}>
        <TableHead><TableRow><TableCell sx={{ fontWeight: 700 }}>Field</TableCell><TableCell sx={{ fontWeight: 700 }}>Value</TableCell></TableRow></TableHead>
        <TableBody>
          <TableRow><TableCell><code>action</code></TableCell><TableCell>BUY, SHORT, SELL_FLAT (= SELL), COVER_FLAT (= COVER).</TableCell></TableRow>
          <TableRow><TableCell><code>strength</code></TableCell><TableCell>HIGH / MEDIUM / LOW on entry signals (a label — it does <strong>not</strong> change position size). Exits carry LOW (unused).</TableCell></TableRow>
          <TableRow><TableCell><code>computationData.tf.score</code></TableCell><TableCell>Chart-TF OverView score at the firing bar (candle colour).</TableCell></TableRow>
          <TableRow><TableCell><code>computationData.htf.zone</code></TableCell><TableCell>HTF background zone at the firing bar (strongBuy / probableBuy / neutral / probableShort / strongShort).</TableCell></TableRow>
          <TableRow><TableCell><code>entryPrice</code></TableCell><TableCell>Close of the firing bar.</TableCell></TableRow>
          <TableRow><TableCell><code>suggestedIsl</code></TableCell><TableCell>Auto-ISL (unchanged): <em>min(low) / max(high) of the previous 2 CLH candles ± Breach Buffer</em>. Seeds Trade.stopLoss.</TableCell></TableRow>
          <TableRow><TableCell><code>suggestedTarget</code></TableCell><TableCell>Auto-Target (unchanged): <em>entry ± N × risk</em>, N from Settings → Target. Seeds Trade.target.</TableCell></TableRow>
          <TableRow><TableCell><code>signalSource</code></TableCell><TableCell><code>ROCKBAND</code></TableCell></TableRow>
        </TableBody>
      </Table>

      <GuideCallout kind="info" title="Chart, scan and backtest are identical">
        The same evaluator drives the Charts overlay markers (B / S / X), the live Strategies scan + Auto-Trade,
        and the Backtest. A signal that prints on the chart will fire in a scan on the same bar, and the
        backtest simulates exactly this rule.
      </GuideCallout>

      <H id="auto-trade">Auto-Trade orchestration</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Scan emits a BUY / SHORT alert with its strength label + Auto-ISL / Auto-Target.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>An entry then passes the gating ladder: global Auto Trade ON → per-symbol <strong>Auto Long / Auto Short</strong> → a per-symbol lock → <strong>no-stacking</strong> (one position per symbol) → <strong>portfolio reserve</strong>. The first failing gate stamps an <em>AUTO SKIPPED</em> chip (<code>AUTO_LONG_OFF</code> / <code>AUTO_SHORT_OFF</code> / <code>POSITION_OPEN</code> / <code>RESERVE</code>) and stops here.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>A fresh entry creates the Trade + a single full-size order, seeded with Auto-ISL / Auto-Target (no scale-in). A SELL_FLAT / COVER_FLAT closes the matching open trade; an opposite entry flips it. Exits bypass the kill switch + daily-loss cap.</>} />
        </ListItem>
      </List>
      <GuideCallout kind="info">
        For the full stage-by-stage auto-trade flow with computed-quantity examples and a scenario matrix,
        see the dedicated <strong>AutoTrade Lifecycle</strong> section.
      </GuideCallout>

      <H id="when-to-use">When RockBand outperforms</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Trending markets where the chart-TF score pushes through ±Strong while the HTF background agrees (the High-strength setup)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText primary="Symbols with enough history for the OverView EMA(200) to warm up on BOTH the chart-TF and the HTF — established equities, BTCUSD / ETHUSD, NIFTY / BANK NIFTY." />
        </ListItem>
      </List>

      <GuideCallout kind="warning" title="Repaint note (autotrade)">
        Closed HTF periods never repaint. Only candles inside the <em>currently-forming</em> HTF bar can
        shift colour as that HTF bar develops — the same property as the on-chart HTF background. Each scan
        decides on closed chart-TF bars and logs/places the signal once at bar close.
      </GuideCallout>

      <H id="setup">How to enable RockBand</H>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Open <OpenInApp to="/strategies" label="Strategies → RockBand" />.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Check your <strong>Timeframe Mapping</strong> (Strategies page) — it decides the HTF used for the background, and therefore for signals.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Create / pick a Watchlist and add symbols. Or pick <strong>★ My favourites</strong> (top of the Watchlist dropdown on the Strategies page): it is the symbols you have starred, re-read at every scan, so starring or un-starring a symbol changes what the next scheduled run covers. It cannot be edited as a list.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Use <strong>Adhoc Scan</strong> to test a date + interval first; the per-symbol status shows the TF score, HTF zone, position and any fresh signal + strength.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Configure <strong>Scheduled Scans</strong> with Route to Dashboard / Send Telegram per taste. A strategy can run N independent schedules (different watchlist + interval combos) in parallel.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>For each instrument you want Auto-Trade on, switch <strong>Auto Long</strong> (BUY) and/or <strong>Auto Short</strong> (SHORT) ON in the Instruments page.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Tune the <code>strong</code> threshold (default 60) on the Strategies page / Chart Settings → OverView if you want stricter or looser Green/Red candles + zone boundaries.</>} />
        </ListItem>
      </List>

      <GuideExample title="A worked RockBand signal">
        90m chart, HTF = 1D. On a closed 90m bar the chart-TF OverView score is +72 → the candle is
        <strong> Green</strong> (Strong Buy). The 1D bar containing that candle is in the <strong>Blue</strong>{' '}
        (Probable Buy) zone, and the daily zone most recently moved up into Blue from Black → trending up.<br />
        → <strong>BUY · Medium</strong> fires. entryPrice = bar close; suggestedIsl = min-low of the previous
        2 CLH candles − Breach Buffer; suggestedTarget = entry + 2 × risk (N = 2).<br />
        Later the 90m candle turns <strong>Red</strong> (score ≤ –60) while still long → <strong>SELL</strong>{' '}
        closes the long (or flips to SHORT if the daily background also qualifies).
      </GuideExample>
    </>
  ),
  searchIndex: [
    { heading: 'Evolution',              anchor: 'intuition',      body: 'evolution bollinger overview cloud retired r96 r97 r114 r119 candle background htf' },
    { heading: 'Building blocks',        anchor: 'building-blocks', body: 'candle colour chart tf zone background htf zone green blue black orange red turned from direction strong probable' },
    { heading: 'Entry signals + strength', anchor: 'entry-rules',  body: 'buy short high medium low strength candle green red background htf zone green blue black orange red turned from up down' },
    { heading: 'Exit signals',           anchor: 'exit-rules',     body: 'sell cover exit long short flip candle red green tsl' },
    { heading: 'Signal payload',         anchor: 'signal-payload', body: 'signal payload action strength tf score htf zone suggested isl suggested target' },
    { heading: 'Auto-Trade orchestration', anchor: 'auto-trade',   body: 'auto trade execute direction gate auto long auto short auto skip reason flip' },
    { heading: 'When RockBand outperforms', anchor: 'when-to-use', body: 'when outperforms trending overview htf background warmed up' },
    { heading: 'How to enable',          anchor: 'setup',          body: 'setup enable rockband timeframe mapping watchlist my favourites starred adhoc scan scheduled scan auto long auto short strong threshold' },
  ],
};

export default Section_strategy_rockband;
